import React, { useState, useEffect, useCallback } from 'react';
import './HRAnnouncements.css';

const BASE = 'https://fotileams.com/api';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
}

const TYPE_OPTIONS = [
  { value: 'info',    label: '📢 General Info',  color: '#3b82f6' },
  { value: 'urgent',  label: '🚨 Urgent',         color: '#ef4444' },
  { value: 'holiday', label: '🎉 Holiday',         color: '#f59e0b' },
  { value: 'event',   label: '🎊 Event',           color: '#8b5cf6' },
];

function getTypeStyle(type) {
  return TYPE_OPTIONS.find(t => t.value === type) || TYPE_OPTIONS[0];
}

export default function HRAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showForm, setShowForm]           = useState(false);
  const [title, setTitle]                 = useState('');
  const [message, setMessage]             = useState('');
  const [type, setType]                   = useState('info');
  const [expiresAt, setExpiresAt]         = useState('');
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState('');

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const res  = await fetch(`${BASE}/announcements/all`, { headers: authHeaders() });
      const data = await res.json();
      setAnnouncements(data.announcements || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async () => {
    setError('');
    if (!title.trim() || !message.trim()) { setError('Title and message are required'); return; }
    setSaving(true);
    try {
      const res  = await fetch(`${BASE}/announcements/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ title, message, type, expires_at: expiresAt || null }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed'); return; }
      setSuccess('Announcement sent to all employees!');
      setTitle(''); setMessage(''); setType('info'); setExpiresAt('');
      setShowForm(false);
      fetchAll();
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Server error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this announcement?')) return;
    await fetch(`${BASE}/announcements/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchAll();
  };

  const isExpired = (ann) => {
    if (!ann.expires_at) return false;
    return new Date(ann.expires_at) < new Date();
  };

  const active  = announcements.filter(a => a.is_active && !isExpired(a));
  const expired = announcements.filter(a => !a.is_active || isExpired(a));

  return (
    <div className="ann-root">
      <div className="ann-header">
        <div>
          <h1 className="ann-title">📢 Announcements</h1>
          <p className="ann-subtitle">Send notices to all employees — shows as popup when they open the app</p>
        </div>
        <button className="ann-new-btn" onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Cancel' : '+ New Announcement'}
        </button>
      </div>

      {success && <div className="ann-success">{success}</div>}

      {/* Create form */}
      {showForm && (
        <div className="ann-form-card">
          <div className="ann-form-title">New Announcement</div>
          {error && <div className="ann-error">{error}</div>}

          <div className="ann-type-pills">
            {TYPE_OPTIONS.map(t => (
              <button key={t.value}
                className={`ann-type-pill ${type === t.value ? 'ann-type-active' : ''}`}
                style={ type === t.value ? { background: t.color, borderColor: t.color, color: '#fff' } : {} }
                onClick={() => setType(t.value)}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="ann-field">
            <label>Title</label>
            <input type="text" placeholder="e.g. Eid Holiday — Office Closed"
              value={title} onChange={e => setTitle(e.target.value)} maxLength={200} />
          </div>

          <div className="ann-field">
            <label>Message</label>
            <textarea rows={3}
              placeholder="Full announcement details..."
              value={message} onChange={e => setMessage(e.target.value)} />
          </div>

          <div className="ann-field">
            <label>Expires On (optional — leave blank to show indefinitely)</label>
            <input type="date" value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)}
              min={new Date().toISOString().split('T')[0]} />
          </div>

          <button className="ann-send-btn" onClick={handleCreate} disabled={saving}>
            {saving ? 'Sending…' : '📢 Send to All Employees'}
          </button>
        </div>
      )}

      {/* Active announcements */}
      <div className="ann-section-label">
        Active ({active.length})
      </div>

      {loading ? (
        <div className="ann-loading">Loading…</div>
      ) : active.length === 0 ? (
        <div className="ann-empty">No active announcements</div>
      ) : (
        <div className="ann-list">
          {active.map((ann, i) => {
            const ts = getTypeStyle(ann.type);
            return (
              <div key={i} className="ann-card" style={{ borderLeft: `4px solid ${ts.color}` }}>
                <div className="ann-card-header">
                  <div className="ann-card-title-row">
                    <span className="ann-card-type" style={{ background: ts.color + '20', color: ts.color }}>
                      {ts.label}
                    </span>
                    <span className="ann-card-title">{ann.title}</span>
                  </div>
                  <button className="ann-delete-btn" onClick={() => handleDelete(ann.id)}>✕</button>
                </div>
                <div className="ann-card-message">{ann.message}</div>
                <div className="ann-card-meta">
                  By {ann.created_by_name} · {new Date(ann.created_at).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {ann.expires_at && ` · Expires ${new Date(ann.expires_at).toLocaleDateString('en-PK', { day: 'numeric', month: 'short' })}`}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Expired/removed */}
      {expired.length > 0 && (
        <>
          <div className="ann-section-label ann-label-expired">Past ({expired.length})</div>
          <div className="ann-list">
            {expired.map((ann, i) => (
              <div key={i} className="ann-card ann-card-expired">
                <div className="ann-card-header">
                  <span className="ann-card-title ann-title-faded">{ann.title}</span>
                  <span className="ann-expired-badge">Expired</span>
                </div>
                <div className="ann-card-message ann-msg-faded">{ann.message}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}